import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Paper,
} from "@mui/material";
import {
  Description as DocumentIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  AccessTime as WaitingIcon,
} from "@mui/icons-material";
import axios from "axios";
import wsIOClient from "socket.io-client";
import { useAuth } from "../../hooks/auth";
import { getApiBaseUrl } from "../../hooks/api";
import { getWebSocketUrl } from "../../hooks/webhook";
import { flowLog } from "../../utils/flowLog";
import { decodeMultipartFilename } from "../../utils/decodeMultipartFilename";

interface DocumentFile {
  id: number;
  url: string;
  displayName: string;
  mimeType: string;
  createdAt: string;
}

interface MedicalRecord {
  id: number;
  complaint: string;
  history: string;
  exams: string;
  hypothesis: string;
  conduct: string;
  examsRequested: string;
  laboratoryExamsRequested: string;
  healthCertificate: string;
  cid: string;
  currentMedicines: string;
}

interface DocumentsResponse {
  success: boolean;
  valid: boolean;
  hasDocuments?: boolean;
  appointment: {
    id: number;
    date: string;
    status: number;
  };
  patient: {
    id: number;
    name: string;
  };
  doctor: {
    id: number;
    name: string;
    crm: string;
  };
  files: DocumentFile[];
  medicalRecord: MedicalRecord | null;
}

const appointmentHasDocuments = (response: DocumentsResponse) =>
  response.hasDocuments ?? response.files.length > 0;

const CONSULTATION_ENDED_MESSAGE = "MAIS_SAUDE_CONSULTATION_ENDED";
const APPOINTMENT_STATUS_FINISHED = 3;

export const Documents = () => {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { getUserData, isSigned } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DocumentsResponse | null>(null);
  const [waiting, setWaiting] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const maisSaudeReturnUrl = searchParams.get("maisSaudeReturnUrl");
  const maisSaudeOrigin = searchParams.get("maisSaudeOrigin");
  const signedUser = isSigned ? getUserData() : null;
  const internalReturnPath = signedUser?.type === "Doctor" ? "/consultas" : "/";

  const fetchDocuments = useCallback(async () => {
    if (!token) {
      setError("Token de acesso inválido");
      setLoading(false);
      return;
    }

    flowLog("PATIENT][DOCUMENTS", "Carregando documentos da consulta", {
      screen: "DOCUMENTS",
      action: "fetch_documents_start",
      role: signedUser?.type === "Doctor" ? "DOCTOR" : "PATIENT",
      token,
    });

    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.get<DocumentsResponse>(
        `${baseUrl}/integration/documents/${token}`
      );

      if (response.data.success) {
        setData(response.data);
        const hasDocuments = appointmentHasDocuments(response.data);
        const isAppointmentFinished =
          response.data.appointment.status === APPOINTMENT_STATUS_FINISHED;

        flowLog("PATIENT][DOCUMENTS", "Documentos carregados", {
          screen: "DOCUMENTS",
          action: "fetch_documents_success",
          role: signedUser?.type === "Doctor" ? "DOCTOR" : "PATIENT",
          consultationId: response.data.appointment.id,
          appointmentId: response.data.appointment.id,
          hasDocuments,
          documentsCount: response.data.files.length,
          documentTypes: response.data.files.map((file) => file.mimeType),
          isAppointmentFinished,
          waitingForDocuments: !hasDocuments && !isAppointmentFinished,
        });

        if (!hasDocuments && !isAppointmentFinished) {
          setWaiting(true);
        } else {
          setWaiting(false);
        }
      } else {
        setError("Token de acesso inválido ou expirado");
      }
    } catch (err: any) {
      console.error("Error fetching documents:", err);
      setError(
        err.response?.data?.message ||
          "Erro ao carregar documentos. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  }, [token, signedUser?.type]);

  useEffect(() => {
    document.documentElement.lang = "pt-BR";
    document.documentElement.setAttribute("translate", "no");
    document.documentElement.classList.add("notranslate");
    document.body.classList.add("notranslate");
    document.body.setAttribute("translate", "no");

    return () => {
      document.body.classList.remove("notranslate");
      document.body.removeAttribute("translate");
    };
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Auto-refresh every 10 seconds when waiting for documents
  useEffect(() => {
    if (!waiting) return;

    const interval = setInterval(() => {
      fetchDocuments();
    }, 10000);

    return () => clearInterval(interval);
  }, [waiting, fetchDocuments]);

  // Real-time socket listener: refresh immediately when appointment-finished fires
  useEffect(() => {
    if (!waiting || !data?.appointment?.id) return;

    const socketConn = wsIOClient(getWebSocketUrl(), {
      extraHeaders: { room: String(data.appointment.id) },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketConn.emit("joinRoom", { appointmentId: data.appointment.id });
    socketConn.on("appointment-finished", () => {
      flowLog("PATIENT][DOCUMENTS", "Evento socket appointment-finished — atualizando documentos", {
        screen: "DOCUMENTS",
        action: "socket_appointment_finished",
        role: signedUser?.type === "Doctor" ? "DOCTOR" : "PATIENT",
        consultationId: data.appointment.id,
        appointmentId: data.appointment.id,
      });
      fetchDocuments();
    });

    flowLog("PATIENT][DOCUMENTS", "Listener socket registrado (aguardando documentos)", {
      screen: "DOCUMENTS",
      action: "socket_listener_registered",
      role: signedUser?.type === "Doctor" ? "DOCTOR" : "PATIENT",
      consultationId: data.appointment.id,
    });

    return () => {
      flowLog("PATIENT][DOCUMENTS", "Listener socket removido (cleanup)", {
        screen: "DOCUMENTS",
        action: "socket_listener_removed",
        consultationId: data.appointment.id,
      });
      socketConn.disconnect();
    };
  }, [waiting, data?.appointment?.id, fetchDocuments, signedUser?.type]);

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes("pdf")) return <PdfIcon color="error" />;
    if (mimeType.includes("image")) return <ImageIcon color="primary" />;
    return <DocumentIcon color="action" />;
  };

  const handleDownload = (file: DocumentFile) => {
    // Files are stored as public-read on DigitalOcean Spaces
    // The file.url from the API already contains the full public URL
    window.open(file.url, "_blank");
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const buildReturnUrl = () => {
    if (!maisSaudeReturnUrl) return null;
    try {
      const url = new URL(maisSaudeReturnUrl);
      url.searchParams.set("hasDocuments", hasDocuments ? "1" : "0");
      return url.toString();
    } catch {
      return maisSaudeReturnUrl;
    }
  };

  const returnToMaisSaude = () => {
    const returnUrl = buildReturnUrl();
    if (returnUrl) {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          {
            type: CONSULTATION_ENDED_MESSAGE,
            appointmentId: data?.appointment?.id,
          },
          maisSaudeOrigin || "*"
        );

        window.close();

        setTimeout(() => {
          if (!window.closed) {
            window.location.replace(returnUrl);
          }
        }, 300);

        return;
      }

      window.location.replace(returnUrl);
      return;
    }

    navigate("/");
  };

  const handleBackToPatientArea = () => {
    flowLog("PATIENT][DOCUMENTS", 'Botão "Voltar para área do paciente" clicado', {
      screen: "DOCUMENTS",
      action: "back_to_patient_area_clicked",
      role: signedUser?.type === "Doctor" ? "DOCTOR" : "PATIENT",
      consultationId: data?.appointment?.id,
      appointmentId: data?.appointment?.id,
      hasDocuments,
      maisSaudeReturnUrl,
      targetTab: "finished",
    });

    if (maisSaudeReturnUrl) {
      flowLog("PATIENT][DOCUMENTS", "Redirecionamento para /patient (Mais Saúde)", {
        screen: "DOCUMENTS",
        action: "navigate_patient_area",
        role: "PATIENT",
        consultationId: data?.appointment?.id,
        appointmentId: data?.appointment?.id,
        returnUrl: maisSaudeReturnUrl,
        initialTab: "finished",
      });
      returnToMaisSaude();
      return;
    }

    navigate(internalReturnPath);
  };

  const hasDocuments = data ? appointmentHasDocuments(data) : false;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          bgcolor: "#f5f5f5",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6">Carregando documentos...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          p: 3,
          bgcolor: "#f5f5f5",
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Se o problema persistir, entre em contato com o suporte.
        </Typography>
      </Box>
    );
  }

  if (waiting) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 3,
          p: 3,
          bgcolor: "#f5f5f5",
        }}
      >
        <WaitingIcon sx={{ fontSize: 80, color: "#1976d2" }} />
        <Typography variant="h5" textAlign="center">
          Aguardando documentos do médico...
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Os documentos da sua consulta ainda estão sendo preparados pelo
          médico. Esta página será atualizada automaticamente.
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Atualizando a cada 10 segundos...
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleBackToPatientArea}
        >
          Voltar para área do paciente
        </Button>
      </Box>
    );
  }

  if (!hasDocuments) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 3,
          p: 3,
          bgcolor: "#f5f5f5",
        }}
      >
        <DocumentIcon sx={{ fontSize: 80, color: "text.secondary" }} />
        <Typography variant="h5" textAlign="center">
          Nenhum documento disponível para esta consulta.
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Esta consulta foi finalizada sem documentos para exibição.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleBackToPatientArea}
        >
          Voltar para área do paciente
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f5f5",
        py: { xs: 2, sm: 4 },
        px: { xs: 1.5, sm: 2 },
      }}
    >
      <Box sx={{ maxWidth: 800, mx: "auto" }}>
        {/* Header */}
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 3 },
            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
            color: "white",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
          >
            Documentos da Consulta
          </Typography>
          <Typography variant="body1">
            Paciente: <strong>{data?.patient?.name}</strong>
          </Typography>
          <Typography variant="body1">
            Médico: <strong>{data?.doctor?.name}</strong> - CRM:{" "}
            {data?.doctor?.crm}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Data da consulta: {data?.appointment?.date && formatDate(data.appointment.date)}
          </Typography>
        </Paper>

        {/* Files Section */}
        {data?.files && data.files.length > 0 ? (
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: "0.95rem", sm: "1.25rem" }, fontWeight: 600 }}
              >
                Documentos da Consulta
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List disablePadding>
                {data.files.map((file) => (
                  <ListItem
                    key={file.id}
                    disablePadding
                    sx={{
                      bgcolor: "#f9f9f9",
                      borderRadius: 1,
                      mb: 1,
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 1.5, sm: 1 },
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getFileIcon(file.mimeType)}
                      </ListItemIcon>
                      <ListItemText
                        primary={decodeMultipartFilename(file.displayName)}
                        secondary={formatDate(file.createdAt)}
                        primaryTypographyProps={{
                          sx: {
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                            wordBreak: "break-word",
                          },
                        }}
                        secondaryTypographyProps={{
                          sx: { fontSize: { xs: "0.75rem", sm: "0.875rem" } },
                        }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(file)}
                      sx={{
                        alignSelf: { xs: "flex-start", sm: "center" },
                        flexShrink: 0,
                      }}
                    >
                      Baixar
                    </Button>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        ) : (
          <Alert severity="info">
            Nenhum documento disponível para esta consulta.
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <Button
            variant="contained"
            onClick={handleBackToPatientArea}
            sx={{
              bgcolor: "#1976d2",
              color: "white",
              px: 3,
              "&:hover": {
                bgcolor: "#1565c0",
              },
            }}
          >
            Voltar para área do paciente
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Documents;
